import React from 'react';
import { Link } from 'react-router-dom';
import { Crown } from 'lucide-react';
import { Avatar, AvatarFallback } from '../components/Avatar';
import { EmiratiBadge } from '../components/EmiratiBadge';
import { TierBadge } from '../components/TierBadge';
import { TierLadderCard } from '../components/TierLadderCard';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/Table';
import { experts } from '../data/experts';
import { tiers } from '../data/tiers';
import { getInitials } from '../utils/expert';
import { getTier, nextTier, tierScore, tierStyles } from '../utils/tier';
const factors = [{
  label: 'Engagements',
  weight: 35,
  detail: 'Completed BAK engagements, up to 10.'
}, {
  label: 'Feedback score',
  weight: 25,
  detail: 'Average organizer rating out of 5.'
}, {
  label: 'Social influence',
  weight: 25,
  detail: 'AI social engagement score across platforms.'
}, {
  label: 'Tenure',
  weight: 15,
  detail: 'Years active in the network, up to 5.'
}];
export function Tiers() {
  const ranked = experts.map(e => ({
    expert: e,
    score: tierScore(e),
    tier: getTier(e)
  })).sort((a, b) => b.score - a.score);
  return <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-id="element-874" data-mp="30f2rfqo7mii6">
      <div className="flex items-center gap-4" data-id="element-875" data-mp="30d9ho2iavi5y">
        <span className="flex size-12 items-center justify-center rounded-xl bg-primary text-primary-foreground" data-id="element-876" data-mp="2vfzw4xy17qne">
          <Crown className="size-6" aria-hidden="true" data-id="element-877" data-mp="39ezancrtyp5u" />
        </span>
        <div data-id="element-878" data-mp="34h7kooaqxd4a">
          <h1 className="text-2xl font-semibold tracking-tight" data-id="element-879" data-mp="34hpl7x4fydi0">Expert tiers</h1>
          <p className="text-sm text-muted-foreground" data-id="element-880" data-mp="3j5kg9k5x8ty9">
            Experts progress through four tiers based on their activity. Each tier unlocks new privileges.
          </p>
        </div>
      </div>

      <section aria-label="Tier ladder" className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-id="element-881" data-mp="3s60hso33tr0h">
        {tiers.map((t, i) => <TierLadderCard key={t.id} tier={t} previousName={i > 0 ? tiers[i - 1].name : undefined} members={ranked.filter(r => r.tier.id === t.id).map(r => r.expert)} data-id="element-882" data-mp="3o6845wyusuo9" data-mpi={i} />)}
      </section>

      <section aria-labelledby="scoring-heading" className="mt-10" data-id="element-883" data-mp="916r1wt8qnnx">
        <h2 id="scoring-heading" className="text-sm font-semibold" data-id="element-884" data-mp="8ytxsni18qvk">How the tier score works</h2>
        <p className="mt-1 text-xs text-muted-foreground" data-id="element-885" data-mp="dsya7ma2hr41">A 100-point score, recalculated automatically after every engagement and review.</p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-886" data-mp="mrsapnqir1mv">
          {factors.map(f => <div key={f.label} className="rounded-xl border bg-card p-4" data-id="element-887" data-mp="rr4x67dha6ox">
              <div className="flex items-baseline justify-between" data-id="element-888" data-mp="wqhgii554vmr">
                <p className="text-sm font-medium" data-id="element-889" data-mp="wppw83gzukbs">{f.label}</p>
                <p className="text-lg font-semibold tabular-nums" data-id="element-890" data-mp="2tjt4art8te9z">{f.weight}<span className="text-xs text-muted-foreground" data-id="element-891" data-mp="2yhult8b0t5jc"> pts</span></p>
              </div>
              <p className="mt-1 text-xs text-muted-foreground" data-id="element-892" data-mp="1fmq3pdccl5qx">{f.detail}</p>
            </div>)}
        </div>
      </section>

      <section aria-labelledby="ranking-heading" className="mt-10" data-id="element-893" data-mp="1avriq0gezjhc">
        <h2 id="ranking-heading" className="mb-3 text-sm font-semibold" data-id="element-894" data-mp="16ufu9y9a14aq">Expert standings</h2>
        <div className="overflow-hidden rounded-xl border bg-card" data-id="element-895" data-mp="11vbkp6ifx4jv">
          <Table data-id="element-896" data-mp="11x66en1jd0p0">
            <TableHeader data-id="element-897" data-mp="11tibbjgg0686">
              <TableRow className="bg-muted/40" data-id="element-898" data-mp="1ylc934ewdj0n">
                <TableHead className="w-12" data-id="element-899" data-mp="1yksyy9vhnyx6">#</TableHead>
                <TableHead data-id="element-900" data-mp="1ujh2pli40fg0">Expert</TableHead>
                <TableHead data-id="element-901" data-mp="1uks7nonajt8p">Tier</TableHead>
                <TableHead data-id="element-902" data-mp="1pkntw6drpcm6">Score</TableHead>
                <TableHead className="hidden md:table-cell" data-id="element-903" data-mp="1pjcm7vh7bkps">Progress to next tier</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-id="element-904" data-mp="e5r0ylegq39j">
              {ranked.map(({
              expert,
              score,
              tier
            }, i) => {
              const next = nextTier(tier);
              const progress = next ? (score - tier.minScore) / (next.minScore - tier.minScore) * 100 : 100;
              return <TableRow key={expert.id} data-id="element-905" data-mp="1x22dcyzflcty" data-mpi={i}>
                    <TableCell className="tabular-nums text-muted-foreground" data-id="element-906" data-mp="21ulwebgw1065">{i + 1}</TableCell>
                    <TableCell data-id="element-907" data-mp="1o3793sfy6cta">
                      <Link to={`/experts/${expert.id}`} className="flex items-center gap-3 hover:underline" data-id="element-908" data-mp="1o3yy2kf1hcal">
                        <Avatar size="sm" data-id="element-909" data-mp="1t20d8u9cgrgs"><AvatarFallback className="text-[10px]" data-id="element-910" data-mp="1sznuwfzocf37">{getInitials(expert.name)}</AvatarFallback></Avatar>
                        <span className="font-medium" data-id="element-911" data-mp="2ksy2foci5txi">{expert.name}</span>
                        {expert.isEmirati && <EmiratiBadge compact data-id="element-912" data-mp="26t5hyarkk9xz" />}
                      </Link>
                    </TableCell>
                    <TableCell data-id="element-913" data-mp="2brzy14y5jkee"><TierBadge tier={tier} data-id="element-914" data-mp="2brqikbaqgvmy" /></TableCell>
                    <TableCell className="font-semibold tabular-nums" data-id="element-915" data-mp="2bqf6lo74whkr">{score}</TableCell>
                    <TableCell className="hidden md:table-cell" data-id="element-916" data-mp="vcf9ekzob617">
                      <div className="flex items-center gap-3" data-id="element-917" data-mp="10dcwnbzjiq9k">
                        <div className="h-1.5 w-40 rounded-full bg-muted" data-id="element-918" data-mp="stk6w6h18b8l">
                          <div className={`h-1.5 rounded-full ${tierStyles[tier.id].bar}`} style={{
                        width: `${Math.min(100, progress)}%`
                      }} data-id="element-919" data-mp="srr10te8tv1n" />
                        </div>
                        <span className="text-xs text-muted-foreground" data-id="element-920" data-mp="11syvt1hjwdqz">
                          {next ? `${(next.minScore - score).toFixed(1)} pts to ${next.name}` : 'Top tier'}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>;
            })}
            </TableBody>
          </Table>
        </div>
      </section>
    </main>;
}